import axios, { AxiosResponse } from 'axios'
import type {
  User, AuthResponse, Project, ProjectSummary, Dataset, DQARun, DQAViolation,
  ViolationComment, ViolationCreditImpact, DQARule, VVProject, VVDocument,
  VVCheckpoint, VVReport, ConsistencyCheckResult,
  KnowledgeBaseEntry, ApiKey, AuditLogEntry, DQASchedule,
  CorrectionSuggestion, PaginatedResponse, ChatMessage, ChatResponse,
  ProjectCreateBody, ProjectUpdateBody, RuleUpdateBody, RuleCreateBody,
  ViolationListParams, CorrectionListParams, AuditListParams,
  ScheduleCreateBody, ScheduleUpdateBody, ViolationAssignBody, ApiKeyCreateBody,
  NotificationListResponse, CompareLastResult, TrendDataPoint, SubmissionWindowSummary,
  StatusResponse, RetentionApplyResult, CalibrationData, ViolationCommentListResponse,
  WebhookTestResult, FeedbackStats, CorrectionExportResponse,
  ProtocolStats, AnomalyLastRunResponse, PredictResult,
  CompletenessResponse, UpdateLogListResponse,
  FolderConnectResult, DimensionSparklinesResult, VVPortfolioItem,
  VVRfi, VVRfiSeverity, VVRfiStatus,
  VVCar, VVCarSeverity, VVDecision,
  MethodologyAlert, VVBenchmarks, AnchorResult, RegistrySyncResult,
  VVCreditQuantityResult, VVAdditionalityResult, VVPermanenceResult,
  VVSLAResult, VVRegistrySubmission,
} from '../types/api'

// withCredentials: true — sends the HttpOnly session cookie automatically (FINDING-014)
// No Authorization header needed — the backend reads from the cookie first
const api = axios.create({ baseURL: '/api/v1', withCredentials: true })
const apiV2 = axios.create({ baseURL: '/api', withCredentials: true })

api.interceptors.response.use(
  r => r,
  err => {
    const status = err.response?.status
    const url = String(err.config?.url ?? '')
    // Don't redirect on login attempts (wrong password) or session checks (init on load)
    const isLoginAttempt = status === 401 && url.includes('/auth/token')
    const isSessionCheck = status === 401 && url.includes('/auth/me')
    if (status === 401 && !isLoginAttempt && !isSessionCheck) {
      window.location.href = '/login'
    }
    return Promise.reject(err)
  }
)

export const authApi = {
  login: (email: string, password: string): Promise<AxiosResponse<AuthResponse>> => {
    const form = new FormData()
    form.append('username', email)
    form.append('password', password)
    return api.post<AuthResponse>('/auth/token', form)
  },
  logout: (): Promise<AxiosResponse<{ message: string }>> => api.post('/auth/logout'),
  me: (): Promise<AxiosResponse<User>> => api.get<User>('/auth/me'),
  // Password reset
  forgotPassword: (email: string): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/auth/reset-password', { token, password }),
  // MFA
  mfaSetup: (): Promise<AxiosResponse<{ secret: string; otpauth_uri: string }>> =>
    api.get('/auth/mfa/setup'),
  mfaVerifySetup: (code: string): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/auth/mfa/verify-setup', { code }),
  mfaDisable: (password: string, code: string): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/auth/mfa/disable', { password, code }),
  mfaValidate: (partialToken: string, code: string): Promise<AxiosResponse<AuthResponse>> =>
    api.post<AuthResponse>('/auth/mfa/validate', { partial_token: partialToken, code }),
}

export const usersApi = {
  // F021: backend now returns PaginatedResponse — client extracts .items so callers are unchanged
  list: (): Promise<AxiosResponse<User[]>> =>
    api.get<PaginatedResponse<User>>('/auth/users')
      .then(r => ({ ...r, data: r.data.items } as AxiosResponse<User[]>)),
  updateRole: (userId: string, role: string): Promise<AxiosResponse<User>> =>
    api.patch<User>(`/auth/users/${userId}/role`, { role }),
  toggleStatus: (userId: string): Promise<AxiosResponse<User>> =>
    api.patch<User>(`/auth/users/${userId}/status`),
  updatePlatformAccess: (userId: string, access: Record<string, boolean>): Promise<AxiosResponse<User>> =>
    api.patch<User>(`/auth/users/${userId}/platform-access`, access),
  invite: (name: string, email: string, role: string): Promise<AxiosResponse<{ message: string; temp_password?: string }>> =>
    api.post('/auth/users/invite', { name, email, role }),
  // B1-#12: theme persistence
  saveTheme: (theme: 'dark' | 'light'): Promise<AxiosResponse<{ theme: string }>> =>
    api.patch('/auth/me/theme', { theme }),
}

export const projectsApi = {
  list: (includeArchived = false): Promise<AxiosResponse<Project[]>> =>
    api.get<Project[]>('/projects/', { params: { include_archived: includeArchived } }),
  create: (data: ProjectCreateBody): Promise<AxiosResponse<Project>> =>
    api.post<Project>('/projects/', data),
  get: (id: string): Promise<AxiosResponse<Project>> =>
    api.get<Project>(`/projects/${id}`),
  update: (id: string, data: ProjectUpdateBody): Promise<AxiosResponse<Project>> =>
    api.patch<Project>(`/projects/${id}`, data),
  archive: (id: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/projects/${id}`),
  deletePermanent: (id: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/projects/${id}`, { params: { hard: true } }),
  restore: (id: string): Promise<AxiosResponse<{ message: string }>> =>
    api.post(`/projects/${id}/restore`),
  summary: (id: string): Promise<AxiosResponse<ProjectSummary>> =>
    api.get<ProjectSummary>(`/projects/${id}/summary`),
}

export const datasetsApi = {
  // F021: backend now returns PaginatedResponse — client extracts .items so callers are unchanged
  list: (projectId?: string): Promise<AxiosResponse<Dataset[]>> =>
    api.get<PaginatedResponse<Dataset>>('/datasets/', { params: { project_id: projectId } })
      .then(r => ({ ...r, data: r.data.items } as AxiosResponse<Dataset[]>)),
  upload: (projectId: string, file: File): Promise<AxiosResponse<Dataset>> => {
    const form = new FormData()
    form.append('project_id', projectId)
    form.append('file', file)
    return api.post<Dataset>('/datasets/upload', form)
  },
  get: (id: string): Promise<AxiosResponse<Dataset>> =>
    api.get<Dataset>(`/datasets/${id}`),
  profile: (id: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get(`/datasets/${id}/profile`),
  preview: (id: string, rows = 20): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get(`/datasets/${id}/preview`, { params: { rows } }),
  delete: (id: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/datasets/${id}`),
  lineage: (id: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get(`/datasets/${id}/lineage`),
}

export const rulesApi = {
  // F021: backend now returns PaginatedResponse — client extracts .items so callers are unchanged
  list: (projectId?: string, dimension?: string): Promise<AxiosResponse<DQARule[]>> =>
    api.get<PaginatedResponse<DQARule>>('/rules/', { params: { project_id: projectId, dimension } })
      .then(r => ({ ...r, data: r.data.items } as AxiosResponse<DQARule[]>)),
  seed: (projectId: string): Promise<AxiosResponse<{ message: string; seeded?: number }>> =>
    api.post(`/rules/seed/${projectId}`),
  update: (id: string, data: RuleUpdateBody): Promise<AxiosResponse<DQARule>> =>
    api.patch<DQARule>(`/rules/${id}`, data),
  create: (projectId: string, data: RuleCreateBody): Promise<AxiosResponse<DQARule>> =>
    api.post<DQARule>('/rules/', data, { params: { project_id: projectId } }),
  aiGapDetect: (projectId: string, datasetId?: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post('/rules/ai-gap-detect', { project_id: projectId, dataset_id: datasetId }),
  nlBuild: (description: string, projectId?: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post('/rules/nl-build', { description, project_id: projectId }),
  // B1-#8: create a rule pre-populated from a violation
  fromViolation: (violationId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post(`/rules/from-violation/${violationId}`),
}

export const runsApi = {
  create: (datasetId: string, projectId: string, ignoreHardGates?: boolean): Promise<AxiosResponse<DQARun>> =>
    api.post<DQARun>('/runs/', { dataset_id: datasetId, project_id: projectId, ignore_hard_gates: !!ignoreHardGates }),
  get: (id: string): Promise<AxiosResponse<DQARun>> =>
    api.get<DQARun>(`/runs/${id}`),
  forDataset: (datasetId: string): Promise<AxiosResponse<DQARun[]>> =>
    api.get<DQARun[]>(`/runs/dataset/${datasetId}`),
  forProject: (projectId: string, limit = 20): Promise<AxiosResponse<DQARun[]>> =>
    api.get<DQARun[]>(`/runs/project/${projectId}`, { params: { limit } }),
  trend: (projectId: string, limit = 30): Promise<AxiosResponse<TrendDataPoint[]>> =>
    api.get<TrendDataPoint[]>(`/runs/project/${projectId}/trend`, { params: { limit } }),
  predict: (projectId: string, horizon = 5): Promise<AxiosResponse<PredictResult>> =>
    api.get<PredictResult>(`/runs/project/${projectId}/predict`, { params: { horizon } }),
  compareLast: (projectId: string): Promise<AxiosResponse<CompareLastResult>> =>
    api.get<CompareLastResult>(`/runs/project/${projectId}/compare-last`),
  dimensionSparklines: (projectId: string, limit = 20): Promise<AxiosResponse<DimensionSparklinesResult>> =>
    api.get<DimensionSparklinesResult>(`/runs/project/${projectId}/dimension-sparklines`, { params: { limit } }),
  violations: (runId: string): Promise<AxiosResponse<DQAViolation[]>> =>
    api.get<DQAViolation[]>(`/runs/${runId}/violations`),
  report: (runId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get(`/runs/${runId}/report`),
  aiExplain: (runId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get(`/runs/${runId}/ai-explain`),
  aiCompare: (runAId: string, runBId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post('/runs/ai-compare', { run_a_id: runAId, run_b_id: runBId }),
  aiCluster: (runId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post(`/runs/${runId}/ai-cluster`),
  aiReport: (runId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post(`/runs/${runId}/ai-report`),
  submissionChecklist: (runId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get(`/submission/checklist/${runId}`),
}

export const violationsApi = {
  list: (params: ViolationListParams): Promise<AxiosResponse<PaginatedResponse<DQAViolation>>> =>
    api.get<PaginatedResponse<DQAViolation>>('/violations/', { params }),
  get: (id: string): Promise<AxiosResponse<DQAViolation>> =>
    api.get<DQAViolation>(`/violations/${id}`),
  // F014: send status in request body, not as a query parameter
  updateStatus: (id: string, status: string): Promise<AxiosResponse<DQAViolation>> =>
    api.patch<DQAViolation>(`/violations/${id}/status`, { status }),
  aiPlaybook: (violationId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get(`/violations/${violationId}/ai-playbook`),
  assign: (id: string, data: ViolationAssignBody): Promise<AxiosResponse<DQAViolation>> =>
    api.patch<DQAViolation>(`/violations/${id}/assign`, data),
  listComments: (id: string): Promise<AxiosResponse<ViolationCommentListResponse>> =>
    api.get<ViolationCommentListResponse>(`/violations/${id}/comments`),
  addComment: (id: string, message: string): Promise<AxiosResponse<ViolationComment>> =>
    api.post<ViolationComment>(`/violations/${id}/comments`, { message }),
  slaOverdue: (projectId?: string): Promise<AxiosResponse<DQAViolation[]>> =>
    api.get<DQAViolation[]>('/violations/sla-overdue', { params: { project_id: projectId } }),
  creditImpact: (id: string, spotPrice = 50): Promise<AxiosResponse<ViolationCreditImpact>> =>
    api.get<ViolationCreditImpact>(`/violations/${id}/credit-impact`, { params: { spot_price_per_tonne: spotPrice } }),
  // B1-#3: bulk status update
  bulkStatus: (ids: string[], status: string): Promise<AxiosResponse<{ updated: number; status: string }>> =>
    api.patch('/violations/bulk-status', { ids, status }),
  // B1-#7: Excel export (returns a blob URL)
  exportXlsxUrl: (params: { severity?: string; status?: string; dimension?: string } = {}) => {
    const q = new URLSearchParams()
    if (params.severity) q.set('severity', params.severity)
    if (params.status) q.set('status', params.status)
    if (params.dimension) q.set('dimension', params.dimension)
    return `/api/v1/violations/export.xlsx${q.toString() ? '?' + q.toString() : ''}`
  },
}

export const correctionsApi = {
  listRules: (projectId?: string): Promise<AxiosResponse<Record<string, unknown>[]>> =>
    api.get('/corrections/rules', { params: { project_id: projectId } }),
  createRule: (projectId: string, data: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post('/corrections/rules', data, { params: { project_id: projectId } }),
  updateRule: (id: string, is_active: boolean): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.patch(`/corrections/rules/${id}`, null, { params: { is_active } }),
  updateRuleFull: (id: string, data: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.patch(`/corrections/rules/${id}/full`, data),
  deleteRule: (id: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/corrections/rules/${id}`),
  generate: (runId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post(`/corrections/generate/${runId}`),
  listSuggestions: (params: CorrectionListParams): Promise<AxiosResponse<CorrectionSuggestion[]>> =>
    api.get<CorrectionSuggestion[]>('/corrections/suggestions', { params }),
  approve: (suggestionId: string, overrideValue?: unknown, overrideReason?: string): Promise<AxiosResponse<CorrectionSuggestion>> =>
    api.post<CorrectionSuggestion>('/corrections/approve', { suggestion_id: suggestionId, override_value: overrideValue, override_reason: overrideReason }),
  reject: (suggestionId: string, reason?: string): Promise<AxiosResponse<{ message: string }>> =>
    api.post(`/corrections/reject/${suggestionId}`, null, { params: { reason } }),
  bulkApprove: (ids: string[]): Promise<AxiosResponse<{ count: number; approved?: number }>> =>
    api.post('/corrections/bulk-approve', { suggestion_ids: ids }),
  listApproved: (datasetId?: string): Promise<AxiosResponse<CorrectionSuggestion[]>> =>
    api.get<CorrectionSuggestion[]>('/corrections/approved', { params: { dataset_id: datasetId } }),
  apply: (datasetId: string): Promise<AxiosResponse<{ message: string; applied?: number }>> =>
    api.post(`/corrections/apply/${datasetId}`),
  exportCorrected: (datasetId: string): Promise<AxiosResponse<CorrectionExportResponse>> =>
    api.get<CorrectionExportResponse>(`/corrections/export/${datasetId}`),
  rerunDQA: (datasetId: string): Promise<AxiosResponse<DQARun>> =>
    api.post<DQARun>(`/corrections/rerun/${datasetId}`),
  export: (datasetId: string): Promise<AxiosResponse<Blob>> =>
    api.get(`/corrections/export/${datasetId}`),
  aiSuggest: (runId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post(`/corrections/ai-suggest/${runId}`),
  aiAutoApprove: (runId: string): Promise<AxiosResponse<{ approved: number }>> =>
    api.post(`/corrections/ai-auto-approve/${runId}`),
  secondApprove: (suggestionId: string): Promise<AxiosResponse<CorrectionSuggestion>> =>
    api.post<CorrectionSuggestion>(`/corrections/second-approve/${suggestionId}`),
}

export const auditApi = {
  // F021: backend now returns PaginatedResponse — client extracts .items so callers are unchanged
  list: (params?: AuditListParams): Promise<AxiosResponse<AuditLogEntry[]>> =>
    api.get<PaginatedResponse<AuditLogEntry>>('/audit/', { params })
      .then(r => ({ ...r, data: r.data.items } as AxiosResponse<AuditLogEntry[]>)),
}

export const schedulesApi = {
  // F021: backend now returns PaginatedResponse — client extracts .items so callers are unchanged
  list: (projectId?: string): Promise<AxiosResponse<DQASchedule[]>> =>
    api.get<PaginatedResponse<DQASchedule>>('/schedules/', { params: { project_id: projectId } })
      .then(r => ({ ...r, data: r.data.items } as AxiosResponse<DQASchedule[]>)),
  create: (data: ScheduleCreateBody): Promise<AxiosResponse<DQASchedule>> =>
    api.post<DQASchedule>('/schedules/', data),
  update: (id: string, data: ScheduleUpdateBody): Promise<AxiosResponse<DQASchedule>> =>
    api.patch<DQASchedule>(`/schedules/${id}`, data),
  delete: (id: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/schedules/${id}`),
  runNow: (id: string): Promise<AxiosResponse<{ run_id: string; message?: string }>> =>
    api.post(`/schedules/${id}/run-now`),
}

export const aiApi = {
  feedbackStats: (projectId?: string): Promise<AxiosResponse<FeedbackStats>> =>
    api.get<FeedbackStats>('/ai/feedback/stats', { params: { project_id: projectId } }),
}

export const chatApi = {
  send: (projectId: string | null, message: string, conversationHistory: ChatMessage[]): Promise<AxiosResponse<ChatResponse>> =>
    api.post<ChatResponse>('/chat/', { project_id: projectId, message, conversation_history: conversationHistory }),
}

export const notificationsApi = {
  list: (limit = 30, unreadOnly = false): Promise<AxiosResponse<NotificationListResponse>> =>
    api.get<NotificationListResponse>('/notifications/', { params: { limit, unread_only: unreadOnly } }),
  markRead: (id: string): Promise<AxiosResponse<{ message: string }>> =>
    api.patch(`/notifications/${id}/read`),
  markAllRead: (): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/notifications/read-all'),
  clearAll: (): Promise<AxiosResponse<{ ok: boolean; deleted: number }>> =>
    api.delete('/notifications/clear-all'),
}

export const apiKeysApi = {
  // F021: backend now returns PaginatedResponse — client extracts .items so callers are unchanged
  list: (projectId?: string): Promise<AxiosResponse<ApiKey[]>> =>
    api.get<PaginatedResponse<ApiKey>>('/api-keys/', { params: { project_id: projectId } })
      .then(r => ({ ...r, data: r.data.items } as AxiosResponse<ApiKey[]>)),
  create: (data: ApiKeyCreateBody): Promise<AxiosResponse<ApiKey & { key: string }>> =>
    api.post<ApiKey & { key: string }>('/api-keys/', data),
  revoke: (id: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/api-keys/${id}`),
}

export default api

// ── Knowledge Base API ───────────────────────────────────────────────────────
export const kbApi = {
  list:    (params?: { domain?: string; category?: string; severity?: string; active_only?: boolean }): Promise<AxiosResponse<KnowledgeBaseEntry[]>> =>
    api.get<KnowledgeBaseEntry[]>('/knowledge-base/', { params }),
  create:  (data: Omit<KnowledgeBaseEntry, 'id'>): Promise<AxiosResponse<KnowledgeBaseEntry>> =>
    api.post<KnowledgeBaseEntry>('/knowledge-base/', data),
  update:  (id: string, d: Partial<KnowledgeBaseEntry>): Promise<AxiosResponse<KnowledgeBaseEntry>> =>
    api.patch<KnowledgeBaseEntry>(`/knowledge-base/${id}`, d),
  delete:  (id: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/knowledge-base/${id}`),
  domains: (): Promise<AxiosResponse<string[]>> =>
    api.get<string[]>('/knowledge-base/meta/domains'),
}

// ── Anomaly Detection API ────────────────────────────────────────────────────
export const anomalyApi = {
  getThresholds:   (): Promise<AxiosResponse<Record<string, number>>> =>
    api.get('/anomaly/thresholds'),
  runOnDataset:    (datasetId: string, thresholds?: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post(`/anomaly/run/${datasetId}`, thresholds ?? null),
  runInline:       (file: File): Promise<AxiosResponse<Record<string, unknown>>> => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post('/anomaly/run-inline', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  getSample:       (): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/anomaly/sample'),
  recommend:       (payload: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post('/anomaly/recommend', payload),
  submitFeedback:  (payload: Record<string, unknown>): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/anomaly/recommendation-feedback', payload),
  // DB-backed session persistence
  getLastRun:      (datasetId: string): Promise<AxiosResponse<AnomalyLastRunResponse>> =>
    api.get<AnomalyLastRunResponse>(`/anomaly/run/${datasetId}/last`),
  saveRun:         (datasetId: string, p: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post(`/anomaly/run/${datasetId}/save`, p),
  updateProgress:  (datasetId: string, p: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.patch(`/anomaly/run/${datasetId}/progress`, p),
}

// ── V&V Platform API ──────────────────────────────────────────────────────
export const vvApi = {
  // Registries
  listRegistries: (): Promise<AxiosResponse<Record<string, unknown>[]>> =>
    apiV2.get('/v2/vv/registries'),

  // Projects
  listProjects: (): Promise<AxiosResponse<VVProject[]>> =>
    apiV2.get<VVProject[]>('/v2/vv/projects'),
  createProject: (data: { name: string; description?: string; project_developer?: string; location?: string; vintage_year?: number }): Promise<AxiosResponse<VVProject>> =>
    apiV2.post<VVProject>('/v2/vv/projects', data),
  getProject: (id: string): Promise<AxiosResponse<VVProject>> =>
    apiV2.get<VVProject>(`/v2/vv/projects/${id}`),
  updateProject: (id: string, data: Partial<VVProject>): Promise<AxiosResponse<VVProject>> =>
    apiV2.patch<VVProject>(`/v2/vv/projects/${id}`, data),
  deleteProject: (id: string): Promise<AxiosResponse<{ message: string }>> =>
    apiV2.delete(`/v2/vv/projects/${id}`),

  // Documents
  uploadDocument: (projectId: string, file: File, documentType: string): Promise<AxiosResponse<VVDocument>> => {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('document_type', documentType)
    return apiV2.post<VVDocument>(`/v2/vv/projects/${projectId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
  },
  listDocuments: (projectId: string): Promise<AxiosResponse<VVDocument[]>> =>
    apiV2.get<VVDocument[]>(`/v2/vv/projects/${projectId}/documents`),
  deleteDocument: (projectId: string, docId: string): Promise<AxiosResponse<{ message: string }>> =>
    apiV2.delete(`/v2/vv/projects/${projectId}/documents/${docId}`),

  // Verification
  runVerification: (projectId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post(`/v2/vv/projects/${projectId}/verify`),
  listCheckpoints: (projectId: string): Promise<AxiosResponse<VVCheckpoint[]>> =>
    apiV2.get<VVCheckpoint[]>(`/v2/vv/projects/${projectId}/checkpoints`),
  updateCheckpoint: (projectId: string, cpId: string, data: Record<string, unknown>): Promise<AxiosResponse<VVCheckpoint>> =>
    apiV2.patch<VVCheckpoint>(`/v2/vv/projects/${projectId}/checkpoints/${cpId}`, data),

  // AI Document Analysis
  aiAnalyse: (projectId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post(`/v2/vv/projects/${projectId}/ai-analyse`),

  // Reports
  generateReport: (projectId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post(`/v2/vv/projects/${projectId}/report`),
  listReports: (projectId: string): Promise<AxiosResponse<VVReport[]>> =>
    apiV2.get<VVReport[]>(`/v2/vv/projects/${projectId}/reports`),
  downloadReportPDF: (projectId: string, reportId: string): Promise<AxiosResponse<Blob>> =>
    apiV2.get(`/v2/vv/projects/${projectId}/reports/${reportId}/pdf`, { responseType: 'blob' }),

  // ── Smart Folder Connector ───────────────────────────────────────────────
  classifyDocuments: (projectId: string, files: { filename: string; extension: string; content_preview?: string }[]): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post(`/v2/vv/projects/${projectId}/classify-documents`, { files }),
  connectSharepoint: (projectId: string, folderUrl: string, graphToken: string): Promise<AxiosResponse<FolderConnectResult>> =>
    apiV2.post<FolderConnectResult>(`/v2/vv/projects/${projectId}/connect-sharepoint`, { folder_url: folderUrl, graph_token: graphToken }),
  connectS3: (projectId: string, bucket: string, prefix: string): Promise<AxiosResponse<FolderConnectResult>> =>
    apiV2.post<FolderConnectResult>(`/v2/vv/projects/${projectId}/connect-s3`, { bucket, prefix }),
  folderIngest: (projectId: string, files: { name: string; document_type: string; download_url: string; graph_token?: string }[]): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post(`/v2/vv/projects/${projectId}/folder-ingest`, { files }),

  // ── V6: Portfolio + Completeness ─────────────────────────────────────────
  getPortfolio: (): Promise<AxiosResponse<VVPortfolioItem[]>> =>
    apiV2.get<VVPortfolioItem[]>('/v2/vv/portfolio'),
  getCompleteness: (projectId: string): Promise<AxiosResponse<CompletenessResponse>> =>
    apiV2.get<CompletenessResponse>(`/v2/vv/projects/${projectId}/completeness`),

  // ── V6: Expiry ────────────────────────────────────────────────────────────
  getExpiringDocuments: (projectId: string, days = 60): Promise<AxiosResponse<VVDocument[]>> =>
    apiV2.get<VVDocument[]>(`/v2/vv/projects/${projectId}/expiring-documents`, { params: { days } }),
  setDocumentExpiry: (projectId: string, docId: string, expiryDate: string | null): Promise<AxiosResponse<VVDocument>> =>
    apiV2.patch<VVDocument>(`/v2/vv/projects/${projectId}/documents/${docId}/expiry`, { expiry_date: expiryDate }),

  /** Re-assign the document_type of an already-uploaded doc without re-uploading. */
  retypeDocument: (projectId: string, docId: string, documentType: string): Promise<AxiosResponse<VVDocument>> =>
    apiV2.patch<VVDocument>(`/v2/vv/projects/${projectId}/documents/${docId}/retype`, { document_type: documentType }),

  // ── V6: AI Content Validation ─────────────────────────────────────────────
  validateDocument: (projectId: string, docId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post(`/v2/vv/projects/${projectId}/documents/${docId}/validate`),

  saveManualValidation: (projectId: string, docId: string, result: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.patch(`/v2/vv/projects/${projectId}/documents/${docId}/validation-result`, result),

  // ── V6: Cross-document consistency ───────────────────────────────────────
  consistencyCheck: (projectId: string): Promise<AxiosResponse<ConsistencyCheckResult>> =>
    apiV2.post<ConsistencyCheckResult>(`/v2/vv/projects/${projectId}/consistency-check`),

  // ── Document & project lifecycle ─────────────────────────────────────────
  clearDocuments: (projectId: string): Promise<AxiosResponse<{ cleared: number }>> =>
    apiV2.post(`/v2/vv/projects/${projectId}/clear-documents`),

  resetProject: (projectId: string): Promise<AxiosResponse<{ reset: boolean }>> =>
    apiV2.post(`/v2/vv/projects/${projectId}/reset`),

  reExtractDocument: (projectId: string, docId: string): Promise<AxiosResponse<{ status: string; doc_id: string }>> =>
    apiV2.post(`/v2/vv/projects/${projectId}/documents/${docId}/re-extract`),

  replaceDocument: (projectId: string, docId: string, file: File): Promise<AxiosResponse<any>> => {
    const fd = new FormData()
    fd.append('file', file)
    return apiV2.post(`/v2/vv/projects/${projectId}/documents/${docId}/replace`, fd)
  },

  // ── V6: Audit log ─────────────────────────────────────────────────────────
  getAuditLog: (projectId: string): Promise<AxiosResponse<Record<string, unknown>[]>> =>
    apiV2.get(`/v2/vv/projects/${projectId}/audit-log`),

  // ── V6: Comments ──────────────────────────────────────────────────────────
  getComments: (projectId: string, docId: string): Promise<AxiosResponse<Record<string, unknown>[]>> =>
    apiV2.get(`/v2/vv/projects/${projectId}/documents/${docId}/comments`),
  addComment: (projectId: string, docId: string, body: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post(`/v2/vv/projects/${projectId}/documents/${docId}/comments`, { body }),

  // ── V6: Review status ─────────────────────────────────────────────────────
  updateReviewStatus: (projectId: string, docId: string, reviewStatus: string, reviewNotes?: string): Promise<AxiosResponse<VVDocument>> =>
    apiV2.patch<VVDocument>(`/v2/vv/projects/${projectId}/documents/${docId}/review-status`, { review_status: reviewStatus, review_notes: reviewNotes }),

  // ── V6: Time-series ───────────────────────────────────────────────────────
  getTimeseries: (projectId: string, docId: string): Promise<AxiosResponse<Record<string, unknown>[]>> =>
    apiV2.get(`/v2/vv/projects/${projectId}/documents/${docId}/timeseries`),

  // ── V6: Submission package ────────────────────────────────────────────────
  buildSubmissionPackage: (projectId: string): Promise<AxiosResponse<Blob>> =>
    apiV2.post(`/v2/vv/projects/${projectId}/build-submission-package`, {}, { responseType: 'blob' }),

  // ── V6: Notification preferences ─────────────────────────────────────────
  getNotificationPreferences: (): Promise<AxiosResponse<Record<string, boolean>>> =>
    apiV2.get('/v2/vv/notification-preferences'),
  updateNotificationPreferences: (data: Record<string, boolean>): Promise<AxiosResponse<Record<string, boolean>>> =>
    apiV2.put('/v2/vv/notification-preferences', data),

  // ── Credit verification ───────────────────────────────────────────────────
  getCreditVerification: (projectId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.get(`/v2/vv/projects/${projectId}/credit-verification`),

  // ── Baseline vs monitoring comparison ────────────────────────────────────
  getBaselineComparison: (projectId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.get(`/v2/vv/projects/${projectId}/baseline-comparison`),

  // ── Dual sign-off ─────────────────────────────────────────────────────────
  signoffReport: (projectId: string, reportId: string, note?: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post(`/v2/vv/projects/${projectId}/reports/${reportId}/signoff`, { note: note || '' }),

  // ── RFI (Request for Information) ────────────────────────────────────────
  listRfis: (projectId: string): Promise<AxiosResponse<VVRfi[]>> =>
    apiV2.get<VVRfi[]>(`/v2/vv/projects/${projectId}/rfis`),
  createRfi: (projectId: string, data: {
    title: string; body: string; severity: VVRfiSeverity;
    checkpoint_id?: string; assigned_to_name?: string;
  }): Promise<AxiosResponse<VVRfi>> =>
    apiV2.post<VVRfi>(`/v2/vv/projects/${projectId}/rfis`, data),
  updateRfi: (projectId: string, rfiId: string, data: Partial<{
    title: string; body: string; severity: VVRfiSeverity;
    status: VVRfiStatus; response: string; assigned_to_name: string;
  }>): Promise<AxiosResponse<VVRfi>> =>
    apiV2.patch<VVRfi>(`/v2/vv/projects/${projectId}/rfis/${rfiId}`, data),
  deleteRfi: (projectId: string, rfiId: string): Promise<AxiosResponse<void>> =>
    apiV2.delete(`/v2/vv/projects/${projectId}/rfis/${rfiId}`),

  // ── Phase 1: CARs (Corrective Action Requests) ───────────────────────────
  listCars: (projectId: string): Promise<AxiosResponse<VVCar[]>> =>
    apiV2.get<VVCar[]>(`/v2/vv/projects/${projectId}/cars`),
  createCar: (projectId: string, data: {
    severity: VVCarSeverity; title: string; description: string; checkpoint_code?: string
  }): Promise<AxiosResponse<VVCar>> =>
    apiV2.post<VVCar>(`/v2/vv/projects/${projectId}/cars`, data),
  updateCar: (projectId: string, carId: string, data: {
    response?: string; status?: string; closure_note?: string
  }): Promise<AxiosResponse<VVCar>> =>
    apiV2.patch<VVCar>(`/v2/vv/projects/${projectId}/cars/${carId}`, data),
  withdrawCar: (projectId: string, carId: string): Promise<AxiosResponse<void>> =>
    apiV2.delete(`/v2/vv/projects/${projectId}/cars/${carId}`),

  // ── Phase 1: Formal Decision ─────────────────────────────────────────────
  getDecision: (projectId: string): Promise<AxiosResponse<VVDecision | null>> =>
    apiV2.get<VVDecision | null>(`/v2/vv/projects/${projectId}/decision`),
  createDecision: (projectId: string, data: {
    decision: string; findings_summary?: string; conditions?: string[]
  }): Promise<AxiosResponse<VVDecision>> =>
    apiV2.post<VVDecision>(`/v2/vv/projects/${projectId}/decision`, data),

  // ── Phase 2: Credit Quantity ──────────────────────────────────────────────
  getCreditQuantity: (projectId: string): Promise<AxiosResponse<VVCreditQuantityResult | null>> =>
    apiV2.get<VVCreditQuantityResult | null>(`/v2/vv/projects/${projectId}/credit-quantity`),
  computeCreditQuantity: (projectId: string): Promise<AxiosResponse<VVCreditQuantityResult>> =>
    apiV2.post<VVCreditQuantityResult>(`/v2/vv/projects/${projectId}/credit-quantity`),

  // ── Phase 3: Additionality & Permanence ──────────────────────────────────
  getAdditionality: (projectId: string): Promise<AxiosResponse<VVAdditionalityResult | null>> =>
    apiV2.get<VVAdditionalityResult | null>(`/v2/vv/projects/${projectId}/additionality-assessment`),
  runAdditionality: (projectId: string): Promise<AxiosResponse<VVAdditionalityResult>> =>
    apiV2.post<VVAdditionalityResult>(`/v2/vv/projects/${projectId}/additionality-assessment`),
  getPermanence: (projectId: string): Promise<AxiosResponse<VVPermanenceResult | null>> =>
    apiV2.get<VVPermanenceResult | null>(`/v2/vv/projects/${projectId}/permanence-assessment`),
  runPermanence: (projectId: string): Promise<AxiosResponse<VVPermanenceResult>> =>
    apiV2.post<VVPermanenceResult>(`/v2/vv/projects/${projectId}/permanence-assessment`),

  // ── Phase 4: Countersign ──────────────────────────────────────────────────
  countersignDecision: (projectId: string, decisionId: string, data: {
    reviewer_name: string; reviewer_note?: string
  }): Promise<AxiosResponse<{ decision_id: string; countersigned_by: string; countersigned_at: string; signature_hash: string; second_reviewer_note: string | null }>> =>
    apiV2.post(`/v2/vv/projects/${projectId}/decision/${decisionId}/countersign`, data),

  // ── Phase 5: SLA ──────────────────────────────────────────────────────────
  getSla: (projectId: string): Promise<AxiosResponse<VVSLAResult>> =>
    apiV2.get<VVSLAResult>(`/v2/vv/projects/${projectId}/sla`),

  // ── Phase 6: Registry Submission ─────────────────────────────────────────
  listRegistrySubmissions: (projectId: string): Promise<AxiosResponse<VVRegistrySubmission[]>> =>
    apiV2.get<VVRegistrySubmission[]>(`/v2/vv/projects/${projectId}/registry-submissions`),
  registrySubmit: (projectId: string, data: {
    registry_slug: string; notes?: string
  }): Promise<AxiosResponse<VVRegistrySubmission>> =>
    apiV2.post<VVRegistrySubmission>(`/v2/vv/projects/${projectId}/registry-submit`, data),

  // ── Feature #9: Methodology alert ────────────────────────────────────────
  getMethodologyAlert: (projectId: string): Promise<AxiosResponse<MethodologyAlert>> =>
    apiV2.get<MethodologyAlert>(`/v2/vv/projects/${projectId}/methodology-alert`),

  // ── Feature #10: Benchmarks ───────────────────────────────────────────────
  getBenchmarks: (): Promise<AxiosResponse<VVBenchmarks>> =>
    apiV2.get<VVBenchmarks>('/v2/vv/benchmarks'),

  // ── Feature #12: Blockchain anchor ───────────────────────────────────────
  anchorReport: (projectId: string, reportId: string): Promise<AxiosResponse<AnchorResult>> =>
    apiV2.post<AnchorResult>(`/v2/vv/projects/${projectId}/reports/${reportId}/anchor`),

  // ── Feature #8: Registry sync ─────────────────────────────────────────────
  getRegistrySync: (projectId: string): Promise<AxiosResponse<RegistrySyncResult | null>> =>
    apiV2.get<RegistrySyncResult | null>(`/v2/vv/projects/${projectId}/registry-sync`),
  triggerRegistrySync: (projectId: string): Promise<AxiosResponse<RegistrySyncResult>> =>
    apiV2.post<RegistrySyncResult>(`/v2/vv/projects/${projectId}/registry-sync`),
}

// ── Phase 3 APIs ──────────────────────────────────────────────────────────────

export const webhooksApi = {
  get:    (projectId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/webhooks/', { params: { project_id: projectId } }),
  update: (projectId: string, data: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.put('/webhooks/', data, { params: { project_id: projectId } }),
  test:   (projectId: string): Promise<AxiosResponse<WebhookTestResult>> =>
    api.post<WebhookTestResult>('/webhooks/test', null, { params: { project_id: projectId } }),
}

export const calibrationApi = {
  list:    (projectId: string): Promise<AxiosResponse<CalibrationData>> =>
    api.get<CalibrationData>('/calibration/', { params: { project_id: projectId } }),
  create:  (data: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post('/calibration/', data),
  update:  (id: string, d: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.patch(`/calibration/${id}`, d),
  delete:  (id: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/calibration/${id}`),
  overdue: (projectId: string): Promise<AxiosResponse<CalibrationData>> =>
    api.get<CalibrationData>('/calibration/overdue', { params: { project_id: projectId } }),
}

export const submissionApi = {
  checklist:    (runId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get(`/submission/checklist/${runId}`),
  listWindows:  (projectId: string): Promise<AxiosResponse<SubmissionWindowSummary>> =>
    api.get<SubmissionWindowSummary>('/submission/windows', { params: { project_id: projectId } }),
  createWindow: (data: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post('/submission/windows', data),
  updateWindow: (id: string, d: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.patch(`/submission/windows/${id}`, d),
  deleteWindow: (id: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/submission/windows/${id}`),
}

export const statusApi = {
  get: (): Promise<AxiosResponse<StatusResponse>> => api.get<StatusResponse>('/status/'),
}

export const retentionApi = {
  get:    (projectId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get(`/retention/${projectId}`),
  update: (projectId: string, data: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.put(`/retention/${projectId}`, data),
  apply:  (projectId: string): Promise<AxiosResponse<RetentionApplyResult>> =>
    api.post<RetentionApplyResult>(`/retention/${projectId}/apply`),
}

// ── Digest API ────────────────────────────────────────────────────────────────
export const digestApi = {
  getConfig: (projectId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get(`/digest/${projectId}`),
  updateConfig: (projectId: string, data: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.put(`/digest/${projectId}`, data),
  sendNow: (projectId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.post(`/digest/${projectId}/send`),
}

// ── Living Protocol Registry API ─────────────────────────────────────────────
export const protocolsApi = {
  // Registries
  listRegistries: (): Promise<AxiosResponse<Record<string, unknown>[]>> =>
    apiV2.get('/v2/protocols/registries'),
  getRegistry: (id: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.get(`/v2/protocols/registries/${id}`),

  // Protocols
  listProtocols: (params?: { registry_id?: string; status?: string }): Promise<AxiosResponse<Record<string, unknown>[]>> =>
    apiV2.get('/v2/protocols/protocols', { params }),
  getProtocol: (id: string, includeCheckpoints = true): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.get(`/v2/protocols/protocols/${id}`, { params: { include_checkpoints: includeCheckpoints } }),
  createProtocol: (data: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post('/v2/protocols/protocols', data),
  updateProtocol: (id: string, data: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.patch(`/v2/protocols/protocols/${id}`, data),
  publishVersion: (id: string, newVersion: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post(`/v2/protocols/protocols/${id}/publish-version`, null, { params: { new_version: newVersion } }),

  // Checkpoints
  listCheckpoints: (protocolId: string, includeDeprecated = false): Promise<AxiosResponse<Record<string, unknown>[]>> =>
    apiV2.get(`/v2/protocols/protocols/${protocolId}/checkpoints`, { params: { include_deprecated: includeDeprecated } }),
  addCheckpoint: (protocolId: string, data: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post(`/v2/protocols/protocols/${protocolId}/checkpoints`, data),
  updateCheckpoint: (protocolId: string, checkpointId: string, data: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.patch(`/v2/protocols/protocols/${protocolId}/checkpoints/${checkpointId}`, data),
  deprecateCheckpoint: (protocolId: string, checkpointId: string): Promise<AxiosResponse<{ message: string }>> =>
    apiV2.delete(`/v2/protocols/protocols/${protocolId}/checkpoints/${checkpointId}`),

  // AI PDF Ingestion
  ingestPdf: (protocolId: string, file: File): Promise<AxiosResponse<Record<string, unknown>>> => {
    const fd = new FormData()
    fd.append('file', file)
    return apiV2.post(`/v2/protocols/protocols/${protocolId}/ingest-pdf`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  // Website monitor
  checkWebsite: (protocolId: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post(`/v2/protocols/protocols/${protocolId}/check-website`),
  runMonitor: (): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post('/v2/protocols/monitor/run'),

  // Update log
  listUpdateLog: (params?: { protocol_id?: string; status?: string; limit?: number; offset?: number }): Promise<AxiosResponse<UpdateLogListResponse>> =>
    apiV2.get<UpdateLogListResponse>('/v2/protocols/update-log', { params }),
  approveUpdate: (entryId: string, notes?: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post(`/v2/protocols/update-log/${entryId}/approve`, { notes }),
  rejectUpdate: (entryId: string, notes?: string): Promise<AxiosResponse<Record<string, unknown>>> =>
    apiV2.post(`/v2/protocols/update-log/${entryId}/reject`, { notes }),
  deleteUpdateLog: (entryId: string): Promise<AxiosResponse<{ message: string }>> =>
    apiV2.delete(`/v2/protocols/update-log/${entryId}`),

  // Stats
  stats: (): Promise<AxiosResponse<ProtocolStats>> =>
    apiV2.get<ProtocolStats>('/v2/protocols/stats'),
}

// ── Task-28: Data source connectors ──────────────────────────────────────────
export const connectorsApi = {
  list:    (projectId?: string) => api.get('/connectors/', { params: projectId ? { project_id: projectId } : {} }),
  get:     (id: string)         => api.get(`/connectors/${id}`),
  create:  (data: any)          => api.post('/connectors/', data),
  update:  (id: string, data: any) => api.patch(`/connectors/${id}`, data),
  remove:  (id: string)         => api.delete(`/connectors/${id}`),
  test:    (id: string)         => api.post(`/connectors/${id}/test`),
  preview: (id: string, limit = 5) => api.get(`/connectors/${id}/preview`, { params: { limit } }),
}

// ── Task-31: Webhook deliveries ───────────────────────────────────────────────
export const webhookDeliveriesApi = {
  list: (projectId: string, limit = 50) =>
    api.get('/webhooks/deliveries', { params: { project_id: projectId, limit } }),
}

// ── Task-32: Dataset anomaly score ────────────────────────────────────────────
export const datasetAnomalyApi = {
  score: (datasetId: string) => api.get(`/anomaly/dataset/${datasetId}`),
}

// ── Task-33: PDF/Excel export ─────────────────────────────────────────────────
export const reportsApi = {
  exportUrl: (projectId: string, format: 'pdf' | 'xlsx') =>
    `/api/v1/reports/export/${projectId}?format=${format}`,
  sendDigest: (projectId: string, recipientEmail: string, format: 'pdf' | 'xlsx' = 'pdf') =>
    api.post(`/reports/send-digest/${projectId}`, { recipient_email: recipientEmail, format }),
}
